class ProductQuantityCartModule {
  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.form = controller.query('[data-product-form]');
    this.submitButton = controller.query('[data-submit-button]');
    this.paymentButtonWrapper = controller.query('.product-form__payment-button');
    this.variantSelect = controller.query('[data-variant-select]');
    this.quantityInput = controller.query('[data-quantity-input]');
    this.minusButton = controller.query('[data-quantity-minus]');
    this.plusButton = controller.query('[data-quantity-plus]');
    this.dynamicCheckoutSyncToken = 0;
    this.inventorySyncToken = 0;

    if (!this.form) return;

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleCartRefresh = this.handleCartRefresh.bind(this);

    this.initQuantitySelector();
    this.initAjaxAddToCart();
    this.initDynamicCheckoutGuard();
    this.bindInventoryEvents();
    this.syncInventoryLimit();
  }

  bindInventoryEvents() {
    this.section.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('cart:refresh', this.handleCartRefresh);
  }

  handleVariantChange() {
    this.syncInventoryLimit({ forceMinimum: true });
  }

  handleCartRefresh() {
    this.syncInventoryLimit();
  }

  getSelectedVariantOption() {
    return this.variantSelect?.selectedOptions?.[0] || null;
  }

  getDefaultMax() {
    const fallback = Number.parseInt(this.quantityInput?.dataset.defaultMax || '99', 10);
    return Number.isNaN(fallback) || fallback < 1 ? 99 : fallback;
  }

  getMinimumQuantity(variantOption = this.getSelectedVariantOption()) {
    if (!variantOption || !window.CartApi?.getVariantMinimumQuantity) return 1;
    return CartApi.getVariantMinimumQuantity(variantOption);
  }

  isVariantAvailableForPurchase(variantOption = this.getSelectedVariantOption()) {
    if (!variantOption) return true;

    const available = variantOption.dataset.variantAvailable === 'true';
    const canMeetMinimumQuantity = !window.CartApi?.canVariantMeetMinimumQuantity
      || CartApi.canVariantMeetMinimumQuantity(variantOption);

    return available && canMeetMinimumQuantity;
  }

  getMinValue() {
    const min = Number.parseInt(this.quantityInput?.min || '1', 10);
    return Number.isNaN(min) || min < 1 ? 1 : min;
  }

  getMaxValue() {
    const max = Number.parseInt(this.quantityInput?.max || '', 10);
    const fallback = this.getDefaultMax();
    const safeMax = Number.isNaN(max) || max < 1 ? fallback : max;
    return Math.max(safeMax, this.getMinValue());
  }

  // Raw parse of what is currently typed. Returns NaN while the field is empty
  // or mid-edit so typing is never rewritten under the user.
  readQuantityValue() {
    return Number.parseInt(this.quantityInput?.value ?? '', 10);
  }

  // Clamped value for calculations. Never writes back to the input.
  getQuantityValue() {
    const min = this.getMinValue();
    const rawValue = this.readQuantityValue();

    if (Number.isNaN(rawValue)) return min;

    return Math.min(Math.max(rawValue, min), this.getMaxValue());
  }

  // Writes the clamped value back to the input — only on commit (blur, change,
  // Enter, submit), never while the user is still typing.
  commitQuantityValue({ notifyMinimum = false } = {}) {
    if (!this.quantityInput) return 1;

    const min = this.getMinValue();
    const rawValue = this.readQuantityValue();
    const safeValue = this.getQuantityValue();

    this.quantityInput.value = `${safeValue}`;

    if (notifyMinimum && !Number.isNaN(rawValue) && rawValue < min && min > 1) {
      const message = window.CartApi?.getMinimumQuantityMessage
        ? CartApi.getMinimumQuantityMessage(this.getSelectedVariantOption())
        : `Minimum ${min} can be ordered.`;
      window.CartApi?.showToast?.(message, true);
    }

    this.updateQuantityButtonState();
    this.syncDynamicCheckoutState();

    return safeValue;
  }

  updateQuantityButtonState() {
    if (!this.quantityInput) return;

    const value = this.getQuantityValue();
    const min = this.getMinValue();
    const max = this.getMaxValue();

    if (this.minusButton) {
      this.minusButton.disabled = value <= min;
    }

    if (this.plusButton) {
      this.plusButton.disabled = value >= max;
    }
  }

  applyQuantityBounds({ min = 1, max = this.getDefaultMax(), forceValue = false } = {}) {
    if (!this.quantityInput) return;

    const safeMin = Math.max(Number.parseInt(min, 10) || 1, 1);
    const safeMax = Math.max(Number.parseInt(max, 10) || this.getDefaultMax(), safeMin);
    const currentValue = Number.parseInt(this.quantityInput.value, 10);
    const nextValue = forceValue ? safeMin : currentValue;
    const clampedValue = Number.isNaN(nextValue) ? safeMin : Math.min(Math.max(nextValue, safeMin), safeMax);

    this.quantityInput.min = `${safeMin}`;
    this.quantityInput.max = `${safeMax}`;
    this.quantityInput.value = `${clampedValue}`;
    this.updateQuantityButtonState();
  }

  updatePurchaseState({ canPurchase, variantOption = this.getSelectedVariantOption() } = {}) {
    const isPurchasable = typeof canPurchase === 'boolean'
      ? canPurchase
      : this.isVariantAvailableForPurchase(variantOption);

    if (this.submitButton) {
      const availableText = this.submitButton.dataset.addToCartText || 'Add to Cart';
      const soldOutText = this.submitButton.dataset.soldOutText || 'Sold Out';
      this.submitButton.disabled = !isPurchasable;
      this.submitButton.textContent = isPurchasable ? availableText : soldOutText;
    }

    if (this.paymentButtonWrapper) {
      this.paymentButtonWrapper.hidden = !isPurchasable;
    }

    this.form.dataset.minimumPurchaseBlocked = isPurchasable ? 'false' : 'true';
  }

  async syncInventoryLimit({ forceMinimum = false } = {}) {
    if (!this.quantityInput || !window.CartApi) {
      this.updateQuantityButtonState();
      return;
    }

    const variantOption = this.getSelectedVariantOption();
    const defaultMax = this.getDefaultMax();
    const requiredMin = this.getMinimumQuantity(variantOption);
    const syncToken = ++this.inventorySyncToken;

    if (!variantOption) {
      this.quantityInput.dataset.stockRemaining = '';
      this.applyQuantityBounds({ min: 1, max: defaultMax, forceValue: forceMinimum });
      this.updatePurchaseState({ canPurchase: true, variantOption: null });
      this.syncDynamicCheckoutState();
      return;
    }

    if (!CartApi.isVariantStockLimited(variantOption)) {
      this.quantityInput.dataset.stockRemaining = '';
      this.applyQuantityBounds({
        min: requiredMin,
        max: defaultMax,
        forceValue: forceMinimum || this.getQuantityValue() < requiredMin
      });
      this.updatePurchaseState({ variantOption });
      this.syncDynamicCheckoutState();
      return;
    }

    let cart = CartApi.getCartCache();

    if (!Array.isArray(cart?.items)) {
      try {
        cart = await CartApi.getCart();
      } catch (_) {
        if (syncToken !== this.inventorySyncToken) return;
        this.quantityInput.dataset.stockRemaining = '';
        this.applyQuantityBounds({
          min: requiredMin,
          max: defaultMax,
          forceValue: forceMinimum || this.getQuantityValue() < requiredMin
        });
        this.updatePurchaseState({ variantOption });
        this.syncDynamicCheckoutState();
        return;
      }
    }

    if (syncToken !== this.inventorySyncToken) return;

    const inventoryLimit = CartApi.getVariantInventoryLimit(variantOption);
    const inCartQuantity = CartApi.getVariantCartQuantity(cart, variantOption.value);
    const remainingQuantity = Math.max((inventoryLimit ?? defaultMax) - inCartQuantity, 0);
    const canPurchase = remainingQuantity >= requiredMin && this.isVariantAvailableForPurchase(variantOption);
    const nextMin = canPurchase ? requiredMin : Math.max(Math.min(remainingQuantity || 1, defaultMax), 1);
    const nextMax = canPurchase ? Math.max(remainingQuantity, nextMin) : Math.max(remainingQuantity, 1);

    this.quantityInput.dataset.stockRemaining = `${remainingQuantity}`;
    this.applyQuantityBounds({
      min: nextMin,
      max: nextMax,
      forceValue: forceMinimum || !canPurchase || this.getQuantityValue() < nextMin
    });
    this.updatePurchaseState({ canPurchase, variantOption });
    this.syncDynamicCheckoutState();
  }

  initQuantitySelector() {
    if (!this.quantityInput) return;

    this.minusButton?.addEventListener('click', () => {
      const currentValue = this.getQuantityValue();
      const min = this.getMinValue();

      if (currentValue > min) {
        this.quantityInput.value = `${currentValue - 1}`;
      } else {
        this.quantityInput.value = `${min}`;
      }

      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    this.plusButton?.addEventListener('click', () => {
      const currentValue = this.getQuantityValue();
      const max = this.getMaxValue();

      if (currentValue < max) {
        this.quantityInput.value = `${currentValue + 1}`;
      } else {
        this.quantityInput.value = `${max}`;
      }

      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    // While typing, keep whatever is in the field (including an empty field or
    // a partially typed number such as "1" on the way to "12") and only refresh
    // the derived UI state.
    this.quantityInput.addEventListener('input', () => {
      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    this.quantityInput.addEventListener('change', () => {
      this.commitQuantityValue({ notifyMinimum: true });
    });

    this.quantityInput.addEventListener('blur', () => {
      this.commitQuantityValue({ notifyMinimum: true });
    });

    this.quantityInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;

      event.preventDefault();
      this.commitQuantityValue({ notifyMinimum: true });
    });

    this.updateQuantityButtonState();
  }

  initAjaxAddToCart() {
    this.form.addEventListener('submit', async (event) => {
      if (!window.CartApi?.addToCart || !window.CartApi?.refreshCart) return;

      event.preventDefault();

      if (!this.submitButton) return;

      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.classList.add('loading');

      try {
        const quantity = this.commitQuantityValue();
        const formData = new FormData(this.form);
        formData.set('quantity', `${quantity}`);
        const selectedVariantId = this.variantSelect?.value || this.getSelectedVariantOption()?.value || '';
        if (selectedVariantId) {
          formData.set('id', selectedVariantId);
        }

        const cartResponse = await CartApi.addToCart(formData, {
          variantData: this.getSelectedVariantOption(),
        });

        const cart = await CartApi.refreshCart(cartResponse);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: cart || cartResponse }));
        if (!window.SimpleHeaderCart?.open) {
          (
            document.getElementById('simple-header-cart-trigger') ||
            document.getElementById('custom-header-cart-trigger')
          )?.click();
        }
      } catch (error) {
        CartApi.showToast(error.message, true);
      } finally {
        this.submitButton.classList.remove('loading');
        this.updatePurchaseState();
      }
    });
  }

  initDynamicCheckoutGuard() {
    if (!this.paymentButtonWrapper || !window.CartApi?.isHighRiskManagementEnabled?.()) return;

    this.syncDynamicCheckoutState();

    document.addEventListener('cart:refresh', () => {
      this.syncDynamicCheckoutState();
    });

    this.paymentButtonWrapper.addEventListener(
      'click',
      (event) => {
        if (this.paymentButtonWrapper.dataset.highRiskBlocked !== 'true') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        CartApi.showToast(CartApi.getHighRiskLimitMessage(), true);
      },
      true
    );
  }

  async syncDynamicCheckoutState() {
    if (!this.paymentButtonWrapper || !window.CartApi?.isHighRiskManagementEnabled?.()) return;

    const syncToken = ++this.dynamicCheckoutSyncToken;
    const quantity = this.getQuantityValue();
    let cart = CartApi.getCartCache();

    if (!Array.isArray(cart?.items)) {
      try {
        cart = await CartApi.getCart();
      } catch (_) {
        return;
      }
    }

    if (syncToken !== this.dynamicCheckoutSyncToken) return;

    const projectedTotal = CartApi.getItemCount(cart) + quantity;
    const isBlocked = CartApi.cartHitsHighRiskLimit(projectedTotal);
    this.paymentButtonWrapper.dataset.highRiskBlocked = isBlocked ? 'true' : 'false';
    this.paymentButtonWrapper.classList.toggle('product-form__payment-button--blocked', isBlocked);
    this.paymentButtonWrapper.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
  }
}

window.HerbiProductSection.registerModule('quantity-cart', (controller) => {
  return new ProductQuantityCartModule(controller);
});
